/**
 * App-wide request behavior shared by every API client (api.ts and
 * marketplaceApi.ts). Each app registers its own handlers at startup.
 *
 *  - extraHeaders: sent on every request (mobile sends its version and platform)
 *  - refreshToken: used to retry once, silently, after a 401
 *  - onSessionExpired: called if the retry still fails (sign out → login)
 *  - onUpdateRequired: called when the API answers 426 APP_UPDATE_REQUIRED
 */

export interface UpdateRequiredInfo {
  minVersion: string | null;
  storeUrl: string | null;
  platform: string | null;
}

export interface RequestHooks {
  extraHeaders?: () => Record<string, string>;
  refreshToken?: () => Promise<string | null>;
  onSessionExpired?: () => void;
  onUpdateRequired?: (info: UpdateRequiredInfo) => void;
}

let hooks: RequestHooks = {};

export function configureRequestHooks(next: Partial<RequestHooks>) {
  hooks = { ...hooks, ...next };
}

export function getRequestHooks(): RequestHooks {
  return hooks;
}

/** Shown instead of raw "Invalid token (status 401)". Draft copy. */
export const SESSION_EXPIRED_TITLE = 'Signed out.';
export const SESSION_EXPIRED_MESSAGE = 'Sign back in to keep going.';
export const UPDATE_REQUIRED_MESSAGE = 'Update joydrop to keep going.';

/**
 * A 401 means "session expired" — except the API also uses 401 for an
 * unverified email, which the verify-email flow handles itself.
 */
export function isUnverifiedEmail401(message: string | undefined | null): boolean {
  return !!message && /not been verified|verify your email/i.test(message);
}

// Only one sign-out at a time, even if several requests fail together.
let expiring = false;
export function notifySessionExpired() {
  if (expiring) return;
  expiring = true;
  try {
    hooks.onSessionExpired?.();
  } finally {
    setTimeout(() => {
      expiring = false;
    }, 5000);
  }
}
